// src/components/UserDetail.jsx  (updated — มี Tab Info + Menu Permission)
import React, { useState } from "react";
import Button1 from "@material-ui/core/Button";
import { UserMenuTab } from "./UserMenuTab";

function Field({ label, value, mono, isUrl, full }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, gridColumn: full ? "1 / -1" : undefined }}>
      <span style={{
        fontSize: 11, color: "#64748b",
        textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600,
      }}>
        {label}
      </span>
      <span style={{
        fontSize:     14,
        color:        isUrl ? "#1d4ed8" : "#1e293b",
        fontFamily:   mono ? "monospace" : "inherit",
        padding:      "5px 0",
        borderBottom: "1px solid #f1f5f9",
      }}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function BoolBadge({ value, labelOn = "Yes", labelOff = "No" }) {
  return (
    <span style={{
      fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 700,
      background: value ? "#dcfce7" : "#f1f5f9",
      color:      value ? "#16a34a" : "#94a3b8",
    }}>
      {value ? labelOn : labelOff}
    </span>
  );
}

function getInitials(user) {
  const f = user.USER_FIRSTNAME?.[0] ?? "";
  const l = user.USER_LASTNAME?.[0]  ?? "";
  return (f + l).toUpperCase() || "?";
}

// ── Tab bar ───────────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  const tabs = [
    { id: "info",  label: "ℹ️ ข้อมูล User" },
    { id: "menus", label: "🔐 สิทธิ์เมนู"   },
  ];
  return (
    <div style={{
      display:      "flex",
      borderBottom: "1px solid #e2e8f0",
      padding:      "0 20px",
      gap:          4,
      background:   "#fff",
    }}>
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding:      "10px 16px",
            fontSize:     13,
            fontWeight:   active === t.id ? 700 : 400,
            color:        active === t.id ? "#1d4ed8" : "#64748b",
            background:   "none",
            border:       "none",
            borderBottom: active === t.id ? "2px solid #1d4ed8" : "2px solid transparent",
            cursor:       "pointer",
            transition:   "all 0.15s",
            marginBottom: -1,
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function UserDetail({ user, allRoles = [], onEdit, onDelete }) {
  const [activeTab, setActiveTab] = useState("info");

  if (!user) {
    return (
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 10, color: "#94a3b8", padding: 40,
      }}>
        <i className="ti ti-users" style={{ fontSize: 44, opacity: 0.25 }} />
        <p style={{ fontSize: 13 }}>Select a user to view details</p>
      </div>
    );
  }

  const roleName = allRoles.find(r => r.ROLE_ID === user.USER_ROLE_ID)?.ROLE_NAME ?? "";

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>

      {/* ── Header ── */}
      <div style={{
        padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
            background: "#eff6ff", color: "#1d4ed8",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, fontWeight: 700,
          }}>
            {getInitials(user)}
          </div>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", margin: 0 }}>
              {user.USER_FIRSTNAME} {user.USER_LASTNAME}
              {user.USER_NICKNAME && (
                <span style={{ fontSize: 13, fontWeight: 400, color: "#64748b", marginLeft: 6 }}>
                  ({user.USER_NICKNAME})
                </span>
              )}
            </h2>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 2 }}>
              <span style={{ fontSize: 12, color: "#64748b" }}>{user.USER_EMAIL}</span>
              {roleName && (
                <span style={{
                  fontSize: 10, padding: "1px 7px", borderRadius: 10, fontWeight: 700,
                  background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe",
                }}>
                  {roleName}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
          <BoolBadge value={user.USER_ACTIVE} labelOn="Active" labelOff="Inactive" />
          <Button1 variant="outlined" size="small" onClick={onEdit}
            style={{ textTransform: "none", fontSize: 12 }}>
            ✏️ Edit
          </Button1>
          <Button1 variant="outlined" size="small" onClick={onDelete}
            style={{ textTransform: "none", fontSize: 12, borderColor: "#fca5a5", color: "#dc2626" }}>
            🗑️ Delete
          </Button1>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <TabBar active={activeTab} onChange={setActiveTab} />

      {/* ── Tab content ── */}
      <div style={{ flex: 1, overflowY: "auto" }}>

        {/* Info tab */}
        {activeTab === "info" && (
          <div style={{ padding: "20px" }}>

            <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Personal Info</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
              <Field label="First name"  value={user.USER_FIRSTNAME} />
              <Field label="Last name"   value={user.USER_LASTNAME} />
              <Field label="Nickname"    value={user.USER_NICKNAME} />
              <Field label="Emp code"    value={user.EMP_CODE} mono />
              <Field label="Position"    value={user.USER_POSITION} />
              <Field label="Country"     value={user.ORIGIN_COUNTRY} />
            </div>

            <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Contact</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
              <Field label="Email"   value={user.USER_EMAIL} full />
              <Field label="Mobile"  value={user.USER_MOBILE} />
              <Field label="Tel"     value={user.USER_TEL} />
              <Field label="Tel ext" value={user.USER_TEL_EXT} />
            </div>

            <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Role & Team</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
              <Field label="Role" value={roleName ? `${roleName} (ID: ${user.USER_ROLE_ID})` : user.USER_ROLE_ID} />
              <Field label="Sales team" value={user.SALES_TEAM} />
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>BU Leader</span>
                <div style={{ padding: "5px 0", borderBottom: "1px solid #f1f5f9" }}><BoolBadge value={user.BU_LEADER} /></div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Sales Leader</span>
                <div style={{ padding: "5px 0", borderBottom: "1px solid #f1f5f9" }}><BoolBadge value={user.SALES_LEADER} /></div>
              </div>
            </div>

            <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>System</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Field label="CRM UID"         value={user.CRM_UID} mono />
              <Field label="MS365 Object ID" value={user.MS365_OBJECT_ID} mono />
              <Field label="Register date"   value={user.REGISTER_DATE} />
              <Field label="Last login"      value={user.LAST_LOGIN} />
            </div>
          </div>
        )}

        {/* Menu permission tab */}
        {activeTab === "menus" && (
          <UserMenuTab
            userId={user.CRM_UID}
            roleId={user.USER_ROLE_ID}
            roleName={roleName}
          />
        )}
      </div>
    </div>
  );
}
